import type { FC, ReactNode } from 'react'
import type { CartItem } from '@/lib/cart'

import Link from 'next/link'
import { Trash2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { productHref } from '@/lib/slug'
import { formatNumber } from '@/lib/format'
import SmartImage from '@/components/generals/smart-image'
import QuantityStepper from '@/components/store/quantity-stepper'
import ColorDot from '@/components/store/color-dot'
import { Amount } from '@/components/store/price'
import { MAX_ITEM_QUANTITY } from '@/constants/site'

interface CartLineProps {
    item: CartItem
    onQuantityChange: (quantity: number) => void
    onRemove: () => void
    onNavigate?: () => void
    compact?: boolean
}

const CartLine: FC<CartLineProps> = ({ item, onQuantityChange, onRemove, onNavigate, compact = false }): ReactNode => {
    const unavailable = item.stock <= 0
    const limit = Math.max(1, Math.min(item.stock, MAX_ITEM_QUANTITY))

    return (
        <div className={cn('flex gap-4', unavailable && 'opacity-70')}>
            <Link
                href={productHref(item.slug)}
                onClick={onNavigate}
                tabIndex={-1}
                className={cn('relative shrink-0 overflow-hidden rounded-lg bg-muted', compact ? 'h-24 w-20' : 'h-32 w-24 sm:h-36 sm:w-28')}
            >
                <SmartImage src={item.image} alt={item.name} sizes='112px' />
            </Link>

            <div className='flex min-w-0 flex-1 flex-col'>
                <div className='flex items-start justify-between gap-3'>
                    <div className='min-w-0'>
                        <Link href={productHref(item.slug)} onClick={onNavigate} className='line-clamp-2 text-sm leading-6 font-medium hover:underline hover:underline-offset-4'>
                            <span data-mdlfy-cms={`products:${item.productId}:name`}>
                                {item.name}
                            </span>
                        </Link>

                        {(item.size || item.color) && (
                            <div className='mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground'>
                                {item.size && (
                                    <span>
                                        سایز:
                                        {' '}
                                        <span className='font-medium text-foreground'>
                                            {item.size}
                                        </span>
                                    </span>
                                )}

                                {item.color && (
                                    <span className='inline-flex items-center gap-1.5'>
                                        رنگ:
                                        <ColorDot color={item.color} className='size-3' />
                                        <span className='font-medium text-foreground'>
                                            {item.color}
                                        </span>
                                    </span>
                                )}
                            </div>
                        )}
                    </div>

                    <button
                        type='button'
                        onClick={onRemove}
                        aria-label='حذف از سبد خرید'
                        className='-m-1.5 shrink-0 rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-destructive'
                    >
                        <Trash2 className='size-4' />
                    </button>
                </div>

                <div className='mt-auto flex flex-wrap items-end justify-between gap-2 pt-3'>
                    {unavailable ? (
                        <span className='text-xs font-medium text-destructive'>
                            این محصول در حال حاضر ناموجود است
                        </span>
                    ) : (
                        <QuantityStepper value={item.quantity} max={limit} onChange={onQuantityChange} size='sm' />
                    )}

                    <div className='text-end'>
                        <Amount value={item.price * item.quantity} className='text-sm' unitClassName='text-[11px]' />

                        {item.quantity > 1 && !unavailable && (
                            <p className='text-[11px] text-muted-foreground'>
                                هر عدد
                                {' '}
                                {formatNumber(item.price)}
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default CartLine
